<template>
  <UIModal v-model="isOpen" :close-button="!isLoading" :close-on-click-outside="!isLoading">
    <template #header>
      <div class="tw:flex tw:justify-center">
        <span class="tw:font-bold tw:text-white tw:text-3xl">{{ $t('realtyRepairTitle') }}</span>
      </div>
    </template>
    <template #default>
      <div class="tw:flex tw:flex-col tw:justify-center tw:items-center tw:gap-3">
        <div class="tw:flex tw:flex-col tw:items-center tw:gap-6 tw:p-6 tw:w-xl ts:max-w-xl tw:max-sm:max-w-[100dvw]">
          <span class="tw:font-normal tw:text-[#90989D] tw:text-lg">{{ $t('realtyRepairResources') }}</span>
          <div class="tw:flex tw:flex-wrap tw:justify-center tw:items-center tw:gap-2">
            <CraftingMaterial
              v-for="{ quantity, tokenAddress } in totalResources"
              :key="tokenAddress"
              :ingredient="{ tokenAddress, quantity: quantity.toString() }"
              :produce-amount="1"
              @not-enough="(value) => handleNotEnough(tokenAddress, value)"
            />
          </div>
          <p v-if="!isEnoughResources" class="tw:text-red-500">{{ $t('realtyRepairNotEnoughResources') }}</p>
        </div>

        <div class="tw:flex tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:max-w-64">
          <p class="tw:text-sm tw:text-center">{{ $t('realtySeasonPointsDescription') }}</p>
          <div class="tw:flex tw:gap-1">
            <img
              :src="tokens.seasonPoints"
              alt=""
              class="tw:h-[36px] tw:w-[36px] tw:min-w-[36px]"
              height="36"
              width="36"
            />
            <div class="tw:text-lg tw:text-[#eea92e]">{{ seasonPoints }}</div>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <div class="tw:flex tw:justify-center">
        <HalloweenGenericButton :disabled="!isEnoughResources || isLoading" @click="handleRepair">{{
          $t('realtyRepairButton')
        }}</HalloweenGenericButton>
      </div>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import tokens from 'public/img/tokens';
import type { IHouseFixResource, IUserHouseInterface } from '~/stores/rent-event-store';
import { useRentEventStore } from '~/stores/rent-event-store';

const { resources, selectedHouses } = defineProps<{
  resources: IHouseFixResource[];
  selectedHouses: IUserHouseInterface[];
  seasonPoints: string;
}>();

const totalResources = computed<IHouseFixResource[]>(() => {
  const housesByType = selectedHouses
    .filter((h) => h.durability < 100)
    .reduce(
      (acc, house) => {
        acc[house.buildingType] = (acc[house.buildingType] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

  const rawResources = resources
    .map((resource) => {
      const housesCount = housesByType[resource.buildingType] || 0;

      if (housesCount === 0) return null;

      return {
        ...resource,
        quantity: resource.quantity * housesCount
      };
    })
    .filter((resource): resource is IHouseFixResource => resource !== null);

  const totalResources: IHouseFixResource[] = [];
  rawResources.forEach((resource) => {
    if (totalResources.some((r) => r.tokenAddress === resource.tokenAddress)) {
      totalResources.find((r) => r.tokenAddress === resource.tokenAddress)!.quantity += resource.quantity;
    } else {
      totalResources.push(resource);
    }
  });

  return totalResources;
});

const isOpen = defineModel<boolean>({ default: false });

const notEnoughMap = ref<Record<string, boolean>>({});
const handleNotEnough = (tokenAddress: string, value: boolean) => {
  notEnoughMap.value[tokenAddress] = value;
};
const isEnoughResources = computed(() => {
  const addresses = totalResources.value.map((r) => r.tokenAddress);
  if (addresses.length === 0) return false;
  return addresses.every((addr) => notEnoughMap.value[addr] === false);
});

const { fixHouses } = useRentEventStore();
const isLoading = ref(false);
async function handleRepair() {
  if (selectedHouses.filter((h) => h.durability < 100).length === 0 || !isEnoughResources.value) return;
  try {
    isLoading.value = true;
    await fixHouses(selectedHouses.filter((h) => h.durability < 100));

    isOpen.value = false;
  } catch {
    // TODO: handle error
  } finally {
    isLoading.value = false;
  }
}
</script>
